import { useCallback, useEffect, useRef, useState } from 'react';
import { useIonViewWillEnter } from '@ionic/react';
import type { AdminListParams } from '../../../domain/Admin';

const PAGE_SIZE = 50;
const SEARCH_DEBOUNCE_MS = 300;

// Una lista del admin buscada en el servidor y de a una página: el texto se
// busca al dejar de tipear y "Cargar más" trae la página siguiente. `fetch`
// devuelve la página pedida y el total; `deps` vuelve a buscar desde el
// principio cuando cambia un filtro (p. ej. "solo disparadas").
export function useAdminList<T>(
  fetch: (params: AdminListParams) => Promise<{ items: T[]; total: number }>,
  deps: unknown[] = []
) {
  const [query, setQuery] = useState('');
  const [search, setSearch] = useState('');
  const [items, setItems] = useState<T[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const requestRef = useRef(0);
  const fetchRef = useRef(fetch);
  fetchRef.current = fetch;
  const loadedCount = useRef(0);
  // Los filtros de quien la usa, comparados por valor.
  const depsKey = JSON.stringify(deps);

  useEffect(() => {
    const timer = setTimeout(() => setSearch(query.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query]);

  // keep = true: vuelve a traer lo que ya estaba cargado (después de una acción), sin achicar la lista.
  const load = useCallback(
    async (keep: boolean) => {
      const requestId = ++requestRef.current;
      setError(null);
      try {
        const limit = keep ? Math.min(Math.max(loadedCount.current, PAGE_SIZE), 200) : PAGE_SIZE;
        const page = await fetchRef.current({ q: search, offset: 0, limit });
        if (requestRef.current !== requestId) return;
        setItems(page.items);
        setTotal(page.total);
        loadedCount.current = page.items.length;
      } catch (err) {
        if (requestRef.current === requestId) setError(err instanceof Error ? err.message : 'No se pudo cargar la lista.');
      } finally {
        if (requestRef.current === requestId) setLoading(false);
      }
    },
    [search, depsKey]
  );

  useEffect(() => {
    load(false);
  }, [load]);

  // Al volver a la pantalla se actualiza lo que se ve.
  useIonViewWillEnter(() => {
    load(true);
  });

  const loadMore = async () => {
    const requestId = requestRef.current;
    setLoadingMore(true);
    try {
      const page = await fetchRef.current({ q: search, offset: items.length, limit: PAGE_SIZE });
      if (requestRef.current !== requestId) return;
      setItems((prev) => [...prev, ...page.items]);
      setTotal(page.total);
      loadedCount.current += page.items.length;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo cargar más.');
    } finally {
      setLoadingMore(false);
    }
  };

  return { query, setQuery, items, total, loading, loadingMore, error, loadMore, reload: () => load(true) };
}
